import type { PreBookingResponse } from '~/api/prebooking'
import type {
  PrebookingDataLayerMetaValues,
  PrebookingDataLayerObject,
  PrebookingDataLayerValues,
} from '~/types/datalayer'
import { Tracking } from '@meeseeks/centaur-tracking'
import * as Sentry from '@sentry/vue'
import { storeToRefs } from 'pinia'
import { PreBookingRequestVehicleCategoryEnum } from '~/api/prebooking'
import { AB_TEST_VARIANTS, isV3FunnelPath } from '~/composables/config/ab-test'
import { useOfferAdditionalOptions } from '~/composables/offer-additional-options'
import { getOfferDaysDuration } from '~/composables/offer-utils'
import { isRunningOnFree2MoveDomain } from '~/composables/tracking/tracking-scripts'
import { useAbTestStore } from '~/stores/ab-test'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { usePaymentProfileStore } from '~/stores/payment-profile'
import { useReferralStore } from '~/stores/referral'
import { useSubscriptionDateTimeStore } from '~/stores/subscription-datetime'
import { useSubscriptionExtrasStore } from '~/stores/subscription-extras'
import { useSubscriptionOfferStore } from '~/stores/subscription-offer'
import { useSubscriptionVehiclesStore } from '~/stores/subscription-vehicles'
import { useVehiclesStore } from '~/stores/vehicles'

const tracking = new Tracking()
const PREBOOKING_EVENT_NAME = 'prebooking_success'

export function useDataLayer() {
  function pushToDataLayer<T = any>(payload: T) {
    tracking.pushToDataLayer(payload)
  }

  function pushPendingEvents() {
    tracking.sendPendingCustomEventsToDatalayer()
  }

  function pushSuccessfulPrebookingEvent(prebookingResponse: PreBookingResponse) {
    try {
      pushToDataLayer<PrebookingDataLayerObject>({
        ...buildSuccessfulPrebookingDataLayerMetaEvent(),
        ...buildPrebookingDataLayerObject(prebookingResponse),
      })
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  function pushPrebookingEvent<T extends keyof PrebookingEventFields>(
    eventName: T,
    fields: PrebookingEventFields[T],
  ) {
    try {
      pushToDataLayer(buildPrebookingEvent(eventName, fields))
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  function pushPrebookingCategoryEvent() {
    pushPrebookingEvent('prebooking_category', ['operator', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingAddressClickButtonEvent() {
    pushPrebookingEvent('prebooking_address_click_button', ['country', 'city', 'operator', 'duration', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingCarClickButtonEvent() {
    pushPrebookingEvent('prebooking_car_click_button', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingCoverageEvent() {
    pushPrebookingEvent('prebooking_coverage', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingOptionsEvent() {
    pushPrebookingEvent('prebooking_options', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingPersonalEvent() {
    pushPrebookingEvent('prebooking_personal', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance', 'extras', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingCostBreakdownEvent() {
    pushToDataLayer({
      event: 'ga4_event',
      event_name: 'prebooking_cost_breakdown',
      ...getAbTestTrackingValues(),
    })
  }

  function pushPrebookingServiceEvent() {
    pushPrebookingEvent('prebooking_service', ['operator', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingWelcomeEvent() {
    pushPrebookingEvent('prebooking_welcome', ['operator', 'experiment_variant', 'prebooking_version'])
  }

  function pushPrebookingPickupAddressEvent() {
    pushPrebookingEvent('prebooking_pickup_address', ['country', 'city', 'experiment_variant', 'prebooking_version'])
  }

  const ONBOARDING_TUTORIAL_SLIDE_COUNT = 4

  function pushPrebookingTutorialEvent(step: number) {
    const isValidTutorialStep = step >= 1 && step <= ONBOARDING_TUTORIAL_SLIDE_COUNT
    if (!isValidTutorialStep) {
      return
    }
    pushToDataLayer({
      event: 'ga4_event',
      event_name: `prebooking_tutorial_${step}`,
      ...getAbTestTrackingValues(),
    })
  }

  function pushSubscriptionEvent<T extends keyof SubscriptionEventFields>(
    eventName: T,
    fields: SubscriptionEventFields[T],
  ) {
    try {
      pushToDataLayer(buildSubscriptionEvent(eventName, fields))
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  function pushSubscriptionServiceEvent() {
    pushSubscriptionEvent('subscription_service', ['country', 'city', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionSearchEvent() {
    pushSubscriptionEvent('subscription_search', ['country', 'city', 'duration_months', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionCategoryEvent() {
    pushSubscriptionEvent('subscription_category', ['country', 'city', 'duration_months', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionMileageEvent() {
    pushSubscriptionEvent('subscription_mileage', ['country', 'city', 'vehicle_category', 'duration_months', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionCoverageEvent() {
    pushSubscriptionEvent('subscription_coverage', ['country', 'city', 'vehicle_category', 'duration_months', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionOptionsEvent() {
    pushSubscriptionEvent('subscription_options', ['country', 'city', 'vehicle_category', 'duration_months', 'insurance', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionConfirmEvent() {
    pushSubscriptionEvent('subscription_confirm', ['country', 'city', 'vehicle_category', 'duration_months', 'insurance', 'deposit', 'monthly_price', 'currency', 'experiment_variant', 'prebooking_version'])
  }

  function pushSubscriptionSuccessEvent(subscriptionId: string, shouldRegister: boolean) {
    try {
      pushToDataLayer({
        ...buildSubscriptionEvent('subscription_success', ['country', 'city', 'vehicle_category', 'duration_months', 'insurance', 'deposit', 'monthly_price', 'currency', 'experiment_variant', 'prebooking_version']),
        subscription_id: subscriptionId,
        new_user: shouldRegister ? 'true' : 'false',
      })
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  return {
    pushPendingEvents,
    pushSuccessfulPrebookingEvent,
    pushToDataLayer,
    pushPrebookingCategoryEvent,
    pushPrebookingAddressClickButtonEvent,
    pushPrebookingCarClickButtonEvent,
    pushPrebookingCoverageEvent,
    pushPrebookingOptionsEvent,
    pushPrebookingPersonalEvent,
    pushPrebookingCostBreakdownEvent,
    pushPrebookingServiceEvent,
    pushPrebookingWelcomeEvent,
    pushPrebookingTutorialEvent,
    pushPrebookingPickupAddressEvent,
    pushSubscriptionServiceEvent,
    pushSubscriptionSearchEvent,
    pushSubscriptionCategoryEvent,
    pushSubscriptionMileageEvent,
    pushSubscriptionCoverageEvent,
    pushSubscriptionOptionsEvent,
    pushSubscriptionConfirmEvent,
    pushSubscriptionSuccessEvent,
  }
}

function getAbTestTrackingValues(): { prebooking_version?: string, experiment_variant?: string } {
  if (typeof window === 'undefined' || !isV3FunnelPath(window.location.pathname))
    return {}

  const { param } = useAbTestStore()
  if (!param)
    return {}

  const tag = AB_TEST_VARIANTS[param].tag
  return {
    prebooking_version: tag,
    experiment_variant: tag,
  }
}

function collectCommonPrebookingData() {
  const locationStore = useLocationStore()
  const referralStore = useReferralStore()
  const vehiclesStore = useVehiclesStore()
  const extrasStore = useExtrasStore()

  const { city } = storeToRefs(locationStore)

  const operator = referralStore.isMetaFlow ? referralStore.leadPlatform : undefined

  const offerDuration = getOfferDaysDuration()
  let duration = offerDuration

  if (!duration) {
    const dateTimeStore = useDateTimeStore()
    if (dateTimeStore.startDate.dateTime && dateTimeStore.endDate.dateTime) {
      const startDate = new Date(dateTimeStore.startDate.dateTime)
      const endDate = new Date(dateTimeStore.endDate.dateTime)
      const timeDiff = endDate.getTime() - startDate.getTime()
      duration = Math.ceil(timeDiff / (1000 * 3600 * 24))
    }
  }

  const selectedOffer = useOfferStore().selectedOffer
  const vehicleCategory = vehiclesStore.getVehicleCategoryData()?.vehicleCategory

  const { option: plusOption } = useOfferAdditionalOptions('plus_insurance')
  const insurance = extrasStore.propertyChosen(plusOption.value?.id) ? 'plus_insurance' : 'basic_insurance'

  const { option: crossBorderOption } = useOfferAdditionalOptions(`cross_border_${city.value.country}`, '.')
  const { option: comfortOption } = useOfferAdditionalOptions('cleaned', '\n', 'refueled')
  const { option: additionalDriverOption } = useOfferAdditionalOptions('additional_driver', '\n')

  return {
    country: city.value.country,
    city: city.value.id,
    operator,
    duration,
    vehicle_category: vehicleCategory ? PreBookingRequestVehicleCategoryEnum[vehicleCategory] : undefined,
    build_series: selectedOffer?.buildSeries,
    insurance,
    extras: {
      comfort_package: extrasStore.propertyChosen(comfortOption.value?.id) ? 'true' : 'false',
      cross_border: extrasStore.propertyChosen(crossBorderOption.value?.id) ? 'true' : 'false',
      additional_driver: extrasStore.propertyChosen(additionalDriverOption.value?.id) ? 'true' : 'false',
    },
    ...getAbTestTrackingValues(),
  }
}

function pickEventFields<TBase extends object, TCommon extends object>(
  baseEvent: TBase,
  fields: readonly (keyof TCommon)[],
  commonData: TCommon,
) {
  const eventData: any = { ...baseEvent }

  fields.forEach((field) => {
    if (field in commonData) {
      eventData[field] = commonData[field]
    }
  })

  return eventData
}

interface PrebookingEventFields {
  prebooking_category: ['operator', 'experiment_variant', 'prebooking_version']
  prebooking_address_click_button: ['country', 'city', 'operator', 'duration', 'experiment_variant', 'prebooking_version']
  prebooking_car_click_button: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'experiment_variant', 'prebooking_version']
  prebooking_coverage: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'experiment_variant', 'prebooking_version']
  prebooking_options: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance', 'experiment_variant', 'prebooking_version']
  prebooking_personal: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance', 'extras', 'experiment_variant', 'prebooking_version']
  prebooking_service: ['operator', 'experiment_variant', 'prebooking_version']
  prebooking_welcome: ['operator', 'experiment_variant', 'prebooking_version']
  prebooking_pickup_address: ['country', 'city', 'experiment_variant', 'prebooking_version']
}

export function buildPrebookingEvent<T extends keyof PrebookingEventFields>(
  eventName: T,
  fields: PrebookingEventFields[T],
) {
  const baseEvent = {
    event: 'ga4_event' as const,
    event_name: eventName,
    credit_back_percentage: usePaymentProfileStore().activeCreditBack?.percentage,
  }

  if (eventName === 'prebooking_category' || eventName === 'prebooking_service' || eventName === 'prebooking_welcome') {
    const referralStore = useReferralStore()
    return {
      ...baseEvent,
      operator: referralStore.isMetaFlow ? referralStore.leadPlatform : undefined,
      ...getAbTestTrackingValues(),
    }
  }

  return pickEventFields(baseEvent, fields, collectCommonPrebookingData())
}

function collectCommonSubscriptionData() {
  const locationStore = useLocationStore()
  const offerStore = useSubscriptionOfferStore()
  const dateTimeStore = useSubscriptionDateTimeStore()
  const extrasStore = useSubscriptionExtrasStore()
  const vehiclesStore = useSubscriptionVehiclesStore()

  const selectedOffer = offerStore.selectedOffer
  const vehicleCategory = selectedOffer?.build_series
    ? (vehiclesStore.getCategory(selectedOffer.build_series) ?? selectedOffer.car_category)
    : selectedOffer?.car_category

  return {
    country: locationStore.city.country,
    city: locationStore.city.id,
    duration_months: dateTimeStore.durationMonths,
    vehicle_category: vehicleCategory,
    insurance: extrasStore.selectedCoverage?.tags?.find(tag => tag.endsWith('_insurance')),
    deposit: selectedOffer?.deposit,
    monthly_price: selectedOffer ? offerStore.getSubscriptionPrice(selectedOffer) + extrasStore.totalExtraPrice : undefined,
    currency: selectedOffer?.currency,
    ...getAbTestTrackingValues(),
  }
}

interface SubscriptionEventFields {
  subscription_service: ['country', 'city', 'experiment_variant', 'prebooking_version']
  subscription_search: ['country', 'city', 'duration_months', 'experiment_variant', 'prebooking_version']
  subscription_category: ['country', 'city', 'duration_months', 'experiment_variant', 'prebooking_version']
  subscription_mileage: ['country', 'city', 'vehicle_category', 'duration_months', 'experiment_variant', 'prebooking_version']
  subscription_coverage: ['country', 'city', 'vehicle_category', 'duration_months', 'experiment_variant', 'prebooking_version']
  subscription_options: ['country', 'city', 'vehicle_category', 'duration_months', 'insurance', 'experiment_variant', 'prebooking_version']
  subscription_confirm: ['country', 'city', 'vehicle_category', 'duration_months', 'insurance', 'deposit', 'monthly_price', 'currency', 'experiment_variant', 'prebooking_version']
  subscription_success: ['country', 'city', 'vehicle_category', 'duration_months', 'insurance', 'deposit', 'monthly_price', 'currency', 'experiment_variant', 'prebooking_version']
}

export function buildSubscriptionEvent<T extends keyof SubscriptionEventFields>(
  eventName: T,
  fields: SubscriptionEventFields[T],
) {
  const baseEvent = {
    event: 'ga4_event' as const,
    event_name: eventName,
  }

  return pickEventFields(baseEvent, fields, collectCommonSubscriptionData())
}

function buildSuccessfulPrebookingDataLayerMetaEvent(): PrebookingDataLayerMetaValues {
  return {
    event: isRunningOnFree2MoveDomain() ? 'ga4_event' : 'ce_prebooking_success',
    event_name: PREBOOKING_EVENT_NAME,
  }
}

function buildPrebookingDataLayerObject(prebookingResponse: PreBookingResponse): PrebookingDataLayerValues {
  /*
  Since tracking is loaded before the main component is mounted, we need to create store references during runtime.
  At this point, all information is available in the stores.
   */
  const extraStore = useExtrasStore()
  const { option: plusOption } = useOfferAdditionalOptions('plus_insurance')
  const { option: crossBorderOption } = useOfferAdditionalOptions(`cross_border_${useLocationStore().city.country}`, '.')
  const { option: comfortOption } = useOfferAdditionalOptions('cleaned', '\n', 'refueled')
  const { city } = storeToRefs(useLocationStore())
  const category = useVehiclesStore().getVehicleCategoryData()?.vehicleCategory

  return {
    prebooking_id: prebookingResponse.preBookingUuid,
    country: city.value.country,
    location: city.value.id,
    value: (useOfferStore().selectedOffer?.price ?? 0) + useExtrasStore().totalExtraPrice,
    currency: useOfferStore().selectedOffer?.currency ?? '',
    prebooking_vehicle_category: PreBookingRequestVehicleCategoryEnum[category!],
    prebooking_insurance: extraStore.propertyChosen(plusOption.value?.id) ? 'plus_coverage' : 'basic_coverage',
    prebooking_extras: {
      comfort_package: extraStore.propertyChosen(comfortOption.value?.id) ? 'true' : 'false',
      cross_border: extraStore.propertyChosen(crossBorderOption.value?.id) ? 'true' : 'false',
      additional_driver: prebookingResponse.additionalDriverCode ? 'true' : 'false',
    },
    prebooking_new_user: prebookingResponse.shouldRegister ? 'true' : 'false',
    credit_back_percentage: usePaymentProfileStore().activeCreditBack?.percentage,
    ...getAbTestTrackingValues(),
  }
}
